import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel } from 'components/FormPanel';

import Button from 'primitives/Button';
import LoadingIndicator from 'primitives/LoadingIndicator';
import Toast from 'primitives/Toast';
import useToast from 'primitives/Toast/useToast';

import { formatCurrencyToShort } from 'utils/format-helper';

const INVESTMENT_ENTITIES_QUERY = gql(`
  query FetchInvestmentEntitiesForFundCommitment {
    dashboard {
      id
      investmentEntities {
        id
        name
        status
        type
        createdAt
        updatedAt
      }
      lastUsedInvestmentEntity {
        id
      }
    }
  }
`);

const INVEST_IN_FUND_MUTATION = gql(`
  mutation CreateFundClosing(
    $amount: Float!
    $fundingSource: FundingSourceEnumType!
    $investmentEntityId: ID!
    $fundId: ID!
  ) {
    createFundClosing(
      amount: $amount
      fundingSource: $fundingSource
      investmentEntityId: $investmentEntityId
      fundId: $fundId
    ) {
      id
      amount
      fundingSource
      investmentEntity {
        id
        name
      }
      status
      createdAt
      updatedAt
    }
  }
`);

export default function InvestInFundDialogButton({
  fundId,
  minimumInvestment,
  maximumInvestment,
}: {
  fundId?: string;
  minimumInvestment?: number;
  maximumInvestment?: number;
  fundName?: string;
}) {
  const navigate = useNavigate();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [createClosing, { loading: investLoading, error: investError }] =
    useMutation(INVEST_IN_FUND_MUTATION);

  const { loading, error, data, refetch } = useQuery(INVESTMENT_ENTITIES_QUERY);
  const { showToast, handleToastOpen, handleToastClose } = useToast();

  if (loading) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const investmentEntities = data.dashboard.investmentEntities;
  const lastUsedInvestmentEntity = data.dashboard.lastUsedInvestmentEntity;

  if (!investmentEntities || !investmentEntities[0])
    return <p>Create an investment entity first before accessing deals</p>;

  return (
    <>
      <FormPanel
        loading={investLoading}
        error={investError}
        onSubmit={data => {
          if (!data.investmentEntityId) {
            return;
          }
          if (!fundId) {
            return;
          }
          createClosing({
            variables: {
              amount: data.amount,
              fundingSource: data.fundingSource,
              investmentEntityId: data.investmentEntityId,
              fundId: fundId,
            },
          }).then(() => {
            handleToastOpen();
            setTimeout(() => {
              navigate(`/invest`); // Navigate to portfolio
            }, 1500);
          });
        }}
        buttonRef={buttonRef}
      >
        <FormInput
          type="currency"
          fieldName="amount"
          label="Amount"
          defaultValue={minimumInvestment}
          helperText={
            minimumInvestment
              ? `Minimum investment amount is ${formatCurrencyToShort(minimumInvestment)}`
              : undefined
          }
          validators={{
            required: true,
            minValue: minimumInvestment,
            maxValue: maximumInvestment,
          }}
          conditionsToShow={{
            matches: [{ field: 'fundingSource', condition: '===', value: 'INDIAN_ACCOUNT' }],
          }}
        />
        <FormInput
          type="currency"
          fieldName="amountForNRAccounts"
          label="Amount"
          defaultValue={1000000}
          helperText={`Minimum investment amount is ₹10L for NR accounts.`}
          validators={{
            required: true,
            minValue: 1000000,
          }}
          conditionsToShow={{
            matches: [{ field: 'fundingSource', condition: '!==', value: 'INDIAN_ACCOUNT' }],
          }}
        />
        <FormInput
          type="select"
          fieldName="investmentEntityId"
          label="Investment Entity"
          options={investmentEntities.map(investmentEntity => ({
            label: investmentEntity?.name,
            value: investmentEntity?.id,
            disabled: investmentEntity.status !== 'ACTIVE',
          }))}
          defaultValue={lastUsedInvestmentEntity?.id}
          validators={{
            required: true,
          }}
          span="3"
        />
        <FormInput
          type="select"
          fieldName="fundingSource"
          label="Funding Source"
          options={[
            {
              label: 'Indian Bank Account',
              value: 'INDIAN_ACCOUNT',
            },
            {
              label: 'NRO/FCNR (B) Account',
              value: 'NON_RESIDENT_ORDINARY_ACCOUNT',
            },
            {
              label: 'NRE Account',
              value: 'NON_RESIDENT_EXTERNAL_ACCOUNT',
            },
            {
              label: 'Foreign Bank Account',
              value: 'FOREIGN_ACCOUNT',
            },
          ]}
          defaultValue={'INDIAN_ACCOUNT'}
          validators={{
            required: true,
          }}
          span="3"
        />
        <FormInput
          type="checkbox-group"
          fieldName="terms"
          label=""
          options={[
            {
              label: 'I agree to all terms and conditions',
              value: 'agree',
            },
          ]}
          defaultValue={[]}
          validators={{
            required: true,
          }}
          compact={true}
        />
      </FormPanel>
      <div className="grid grid-cols-2 mt-4">
        <Button
          className="w-full"
          loading={investLoading}
          onClick={() => {
            // @ts-ignore
            buttonRef.current?.submit();
          }}
          variant="primary"
        >
          Invest
        </Button>
      </div>

      <Toast
        type="success"
        title="Commitment Submitted"
        message={
          'We will notify the fund manager of your interest in this Fund. Wiring instructions will be sent to you once the fund manager has confirmed your investment.'
        }
        show={showToast}
        handleClose={handleToastClose}
      />
    </>
  );
}
